/**
 * Sauvegardes d'etat.
 *
 * Le coeur C++ ecrit et relit un fichier ; en WASM ce fichier vit dans MEMFS,
 * le systeme de fichiers en memoire d'emscripten. On fait donc l'aller-retour :
 *
 *   sauvegarder : saveState(chemin) -> lecture MEMFS -> IndexedDB
 *   charger     : IndexedDB -> ecriture MEMFS -> loadState(chemin)
 *
 * localStorage est hors jeu : un etat PS2 pese plusieurs dizaines de Mo (32 Mo
 * de RAM EE, 4 Mo de memoire GS, plus l'IOP), contre ~5 Mo de quota.
 */

import { PlayModule } from './PlayModule';

const DB_NAME = 'ps2-states';
const DB_VERSION = 1;
const STORE = 'states';

export const SLOT_COUNT = 4;

export interface SaveState {
	/** `${gameId}:${slot}` */
	key: string;
	gameId: string;
	slot: number;
	data: Uint8Array;
	/** Vignette JPEG du canevas au moment de la sauvegarde. */
	thumbnail: string | null;
	createdAt: number;
	size: number;
}

function openDb(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE)) {
				db.createObjectStore(STORE, { keyPath: 'key' });
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
	return openDb().then(
		(db) =>
			new Promise<T>((resolve, reject) => {
				const transaction = db.transaction(STORE, mode);
				const request = run(transaction.objectStore(STORE));
				request.onsuccess = () => resolve(request.result);
				request.onerror = () => reject(request.error);
			}),
	);
}

export async function listStates(gameId: string): Promise<(SaveState | null)[]> {
	const slots: (SaveState | null)[] = new Array(SLOT_COUNT).fill(null);
	try {
		const all = await tx<SaveState[]>('readonly', (store) => store.getAll() as any);
		for (const state of all ?? []) {
			if (state.gameId === gameId && state.slot >= 0 && state.slot < SLOT_COUNT) {
				slots[state.slot] = state;
			}
		}
	} catch {
		/* pas de persistance disponible */
	}
	return slots;
}

/**
 * Vignette du canevas, pour reconnaitre l'emplacement d'un coup d'oeil.
 *
 * Le contexte WebGL est cree sans `preserveDrawingBuffer`, donc son tampon est
 * vide des que le navigateur a compose l'image : un drawImage differe rend du
 * noir. Il faut donc capturer pendant le meme frame que le rendu, c'est-a-dire
 * depuis un requestAnimationFrame, avant que la main ne repasse au compositeur.
 */
function grabThumbnail(): Promise<string | null> {
	return new Promise((resolve) => {
		const canvas = document.getElementById('outputCanvas') as HTMLCanvasElement | null;
		if (!canvas) {
			resolve(null);
			return;
		}
		requestAnimationFrame(() => {
			try {
				const scaled = document.createElement('canvas');
				scaled.width = 160;
				scaled.height = 120;
				const ctx = scaled.getContext('2d');
				if (!ctx) {
					resolve(null);
					return;
				}
				ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
				const url = scaled.toDataURL('image/jpeg', 0.6);
				// Une image entierement noire signale une capture ratee : on
				// prefere pas de vignette a une vignette trompeuse.
				const probe = ctx.getImageData(0, 0, scaled.width, scaled.height).data;
				let sum = 0;
				for (let i = 0; i < probe.length; i += 40) sum += probe[i];
				resolve(sum > 0 ? url : null);
			} catch {
				resolve(null);
			}
		});
	});
}

const MEMFS_PATH = '/work/state.tmp';

const OP_PENDING = 1;
const OP_SUCCESS = 2;

/**
 * Attend la fin d'une operation d'etat sans jamais bloquer le fil.
 *
 * Le coeur poste le travail dans la boite aux lettres de la VM, qui est videe
 * par la boucle principale — celle-la meme qui execute ce code. Toute attente
 * synchrone fige donc l'onglet. On rend la main entre chaque scrutation.
 */
function awaitStateOp(timeoutMs = 30000): Promise<boolean> {
	// La boite aux lettres n'est videe que par une VM en marche : si le jeu est
	// en pause, l'operation resterait indefiniment en attente.
	try {
		if (PlayModule.isRunning && !PlayModule.isRunning()) PlayModule.resume?.();
	} catch {
		/* ignore */
	}
	return new Promise((resolve) => {
		const started = Date.now();
		const tick = () => {
			let status: number;
			try {
				status = PlayModule.pollStateOp();
			} catch {
				resolve(false);
				return;
			}
			if (status === OP_PENDING) {
				if (Date.now() - started > timeoutMs) {
					resolve(false);
					return;
				}
				setTimeout(tick, 50);
				return;
			}
			PlayModule.clearStateOp?.();
			resolve(status === OP_SUCCESS);
		};
		setTimeout(tick, 50);
	});
}

export async function saveToSlot(gameId: string, slot: number): Promise<SaveState | null> {
	if (!PlayModule?.beginSaveState) return null;

	const thumbnail = await grabThumbnail();

	PlayModule.beginSaveState(MEMFS_PATH);
	const ok = await awaitStateOp();
	if (!ok) return null;

	let data: Uint8Array;
	try {
		data = PlayModule.FS.readFile(MEMFS_PATH);
		PlayModule.FS.unlink(MEMFS_PATH);
	} catch {
		return null;
	}

	const state: SaveState = {
		key: `${gameId}:${slot}`,
		gameId,
		slot,
		// On recopie : la vue pointe sur le tas WASM, qui bouge.
		data: new Uint8Array(data),
		thumbnail,
		createdAt: Date.now(),
		size: data.length,
	};

	try {
		await tx('readwrite', (store) => store.put(state) as any);
	} catch (error) {
		// Quota depasse : on le signale plutot que d'echouer en silence.
		console.error('Sauvegarde impossible (quota ?)', error);
		return null;
	}
	return state;
}

export async function loadFromSlot(gameId: string, slot: number): Promise<boolean> {
	if (!PlayModule?.beginLoadState) return false;
	let state: SaveState | undefined;
	try {
		state = await tx<SaveState>('readonly', (store) => store.get(`${gameId}:${slot}`) as any);
	} catch {
		return false;
	}
	if (!state) return false;

	try {
		PlayModule.FS.writeFile(MEMFS_PATH, state.data);
	} catch {
		return false;
	}
	PlayModule.beginLoadState(MEMFS_PATH);
	const ok = await awaitStateOp();
	try {
		PlayModule.FS.unlink(MEMFS_PATH);
	} catch {
		/* ignore */
	}
	return ok;
}

export async function deleteSlot(gameId: string, slot: number): Promise<void> {
	try {
		await tx('readwrite', (store) => store.delete(`${gameId}:${slot}`) as any);
	} catch {
		/* ignore */
	}
}

/**
 * Telecharge un etat, pour le transporter d'une machine a l'autre.
 *
 * Le format est celui de la version de bureau : une archive ZIP. On garde donc
 * l'extension .zip, ce qui rend les fichiers interchangeables entre le
 * navigateur et Play! sur ordinateur (Play Data Files/states/*.stN.zip).
 */
export function exportState(state: SaveState, gameTitle: string): void {
	const blob = new Blob([state.data as BlobPart], { type: 'application/zip' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	const stamp = new Date(state.createdAt).toISOString().slice(0, 16).replace(/[:T]/g, '-');
	link.href = url;
	link.download = `${gameTitle.replace(/[^\w-]+/g, '_')}_slot${state.slot + 1}_${stamp}.zip`;
	link.click();
	URL.revokeObjectURL(url);
}

export async function importState(gameId: string, slot: number, file: File): Promise<SaveState | null> {
	const buffer = new Uint8Array(await file.arrayBuffer());
	const state: SaveState = {
		key: `${gameId}:${slot}`,
		gameId,
		slot,
		data: buffer,
		thumbnail: null,
		createdAt: Date.now(),
		size: buffer.length,
	};
	try {
		await tx('readwrite', (store) => store.put(state) as any);
	} catch {
		return null;
	}
	return state;
}

export function formatStateSize(bytes: number): string {
	if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1).replace('.', ',')} Mo`;
	return `${Math.round(bytes / 1024)} Ko`;
}
